<template>
  <aside v-show="open" class="ai-panel" aria-label="Knowledge AI personal chat" @keydown.esc.stop @keyup.esc.stop="escape">
    <header class="ai-panel__header">
      <div>
        <h2>Knowledge AI <AppStatusBadge design="new" tone="blue" compact>Beta</AppStatusBadge></h2>
        <p>Personal chat · No lead context</p>
      </div>
      <AppModalClose design="new" title="Minimize chat" aria-label="Minimize chat" @click="$emit('minimize')" />
    </header>
    <nav v-show="!showFaq" class="ai-panel__navigation" aria-label="Chat navigation">
      <button v-if="showChats" ref="back" class="ai-panel__nav-button" type="button" :disabled="!chat" @click="backToChat">
        <span aria-hidden="true">←</span> Back to chat
      </button>
      <template v-else>
        <button ref="chatsButton" class="ai-panel__nav-button" type="button" @click="openChats">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            <path d="M8 6h12M8 12h12M8 18h12M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          Chats
        </button>
        <span class="ai-panel__chat-title" :title="chat ? chat.title : ''">{{ chat ? shortTitle(chat.title) : 'Your assistant' }}</span>
        <button class="ai-panel__new" type="button" title="New chat" aria-label="New chat" @click="start">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
        </button>
      </template>
    </nav>
    <div v-if="state.error && !showFaq" class="ai-panel__error" role="alert">{{ state.error }} <button type="button" @click="load">Retry</button></div>
    <p v-if="state.loading && !chat && !showFaq" class="ai-panel__loading">Loading chats…</p>
    <ChatFaq v-if="showFaq" @close="closeFaq" />
    <ChatList
      v-show="showChats && !showFaq"
      :sessions="state.sessions"
      :chats="state.chats"
      :selected="state.selected"
      :loading="state.loading"
      :has-more="Boolean(state.nextPage)"
      :sort="state.sort"
      :sort-busy="state.sortBusy"
      :update-chat="controller.updateChat"
      @sort="controller.changeSort($event)"
      @select="select"
      @new="start"
      @more="controller.list(true)"
    />
    <section v-if="chat" v-show="!showChats && !showFaq" class="ai-panel__conversation" aria-label="Current conversation">
      <ChatMessages :key="chat.id" ref="messages" :chat="chat" @older="older" @retry="controller.retry(chat, $event)" />
      <div v-if="chat.error" class="ai-panel__error" role="alert">{{ chat.error }} <button v-if="!chat.pending" type="button" @click="controller.history(chat)">Refresh</button></div>
      <form class="ai-panel__composer" @submit.prevent="send">
        <label class="ai-panel__label" for="knowledge-ai-message">Message</label>
        <textarea id="knowledge-ai-message" ref="composer" v-model="chat.draft" rows="3" maxlength="20000" placeholder="Ask Knowledge AI…" :disabled="chat.sending || Boolean(chat.pending)" @keydown.enter.exact.prevent="sendUnlessComposing" />
        <div class="ai-panel__send">
          <small>{{ chat.draft.length.toLocaleString() }} / 20,000 · Shift + Enter for a new line</small>
          <AppButton design="new" theme="default" type="submit" :disabled="chat.sending || chat.preferencesBusy || (!chat.pending && !chat.draft.trim())">{{ chat.sending ? 'Sending…' : chat.pending ? 'Retry send' : 'Send' }}</AppButton>
        </div>
      </form>
    </section>
    <footer class="ai-panel__help" aria-label="Knowledge AI help">
      <button type="button" title="Create a support ticket" @click="openHelp">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
          <path d="M4 13v-1a8 8 0 0 1 16 0v1M4 12H3v6h3v-6H4Zm16 0h1v6h-3v-6h2Zm0 6v1a2 2 0 0 1-2 2h-5" />
        </svg>
        Need help
      </button>
      <button ref="faqButton" type="button" :aria-expanded="String(showFaq)" @click="showFaq ? closeFaq() : showFaq = true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 1 1 4 2l-1.5 1v2M12 17h.01" />
        </svg>
        FAQ
      </button>
    </footer>
  </aside>
</template>

<script>
import AppModalClose from '@/components/AppModalClose'
import { getKnowledgeChats, postKnowledgeChat, getKnowledgeChatMessages, postKnowledgeChatMessage, postKnowledgeChatRetry, getKnowledgeChatSettings, patchKnowledgeChatSettings, patchKnowledgeChatPreferences } from '@/http'
import ChatMessages from './ChatMessages'
import ChatList from './ChatList'
import ChatFaq from './ChatFaq'
import { shortChatTitle } from './model'
import { createChatController } from './controller'

export default {
  name: 'KnowledgeChatPanel',
  components: { AppModalClose, ChatMessages, ChatList, ChatFaq },
  props: { open: { type: Boolean, default: false } },
  data() {
    const controller = createChatController({
      list: getKnowledgeChats,
      create: postKnowledgeChat,
      history: getKnowledgeChatMessages,
      send: postKnowledgeChatMessage,
      retry: postKnowledgeChatRetry,
      settings: getKnowledgeChatSettings,
      saveSettings: patchKnowledgeChatSettings,
      savePreferences: patchKnowledgeChatPreferences
    }, this.$set.bind(this))
    return { controller, state: controller.state, timer: null, destroyed: false, showChats: false, showFaq: false }
  },
  computed: {
    chat() { return this.state.chats[this.state.selected] || null }
  },
  watch: {
    open: {
      immediate: true,
      handler(value) {
        clearTimeout(this.timer)
        if (value) this.load()
      }
    }
  },
  beforeDestroy() {
    this.destroyed = true
    clearTimeout(this.timer)
  },
  methods: {
    shortTitle: shortChatTitle,
    openHelp() {
      this.$modal.show('support-ticket-create', {
        subject: 'Knowledge AI — help request',
        requester: this.$store.state.userData
      })
    },
    closeFaq() {
      this.showFaq = false
      this.$nextTick(() => { if (this.$refs.faqButton) this.$refs.faqButton.focus() })
    },
    openChats() {
      this.showChats = true
      this.controller.initialize()
      this.$nextTick(() => { if (this.$refs.back) this.$refs.back.focus() })
    },
    backToChat() {
      this.showChats = false
      this.refresh()
      this.focus()
    },
    escape() {
      if (this.showFaq) this.closeFaq()
      else if (this.showChats && this.chat) this.backToChat()
      else this.$emit('minimize')
    },
    async load() {
      await this.controller.initialize()
      await this.refresh()
      this.focus()
    },
    async refresh() {
      clearTimeout(this.timer)
      if (!this.open || this.destroyed) return
      await this.controller.history(this.chat)
      if (this.open && !this.destroyed) this.timer = setTimeout(this.refresh, 5000)
    },
    focus() {
      this.$nextTick(() => {
        if (this.open && !this.showChats && !this.showFaq && this.$refs.composer) this.$refs.composer.focus()
      })
    },
    start() {
      this.showChats = false
      if (this.chat && !this.chat.saved && !this.chat.draft && !this.chat.pending) return this.focus()
      this.controller.start()
      this.focus()
    },
    async select(id) {
      this.showChats = false
      this.state.selected = id
      await this.controller.history(this.chat)
      this.focus()
    },
    async send() {
      const chat = this.chat
      await this.controller.send(chat)
      if (chat !== this.chat || this.showChats || this.showFaq) return
      if (this.$refs.messages) this.$refs.messages.bottom()
      this.focus()
    },
    sendUnlessComposing(event) { if (!event.isComposing) this.send() },
    older() {
      const chat = this.chat
      this.$refs.messages.preserveScroll(() => this.controller.history(chat, true))
    }
  }
}
</script>

<style scoped>
.ai-panel { position: fixed; z-index: 10030; top: 12px; bottom: 12px; right: 12px; width: 504px; max-width: calc(100vw - 24px); display: flex; flex-direction: column; color: #273449; background: white; border: 1px solid #dde4ee; border-radius: 16px; box-shadow: 0 10px 40px #172c4933; overflow: hidden; }
.ai-panel__header { display: flex; align-items: center; justify-content: space-between; padding: 18px 16px 12px; gap: 12px; }
.ai-panel__header h2 { display: flex; align-items: center; gap: 8px; font-size: 18px; font-weight: 600; margin: 0 0 4px; }
.ai-panel__header p { color: #6b778b; font-size: 12px; margin: 0; }
.ai-panel__navigation { display: flex; align-items: center; gap: 10px; padding: 0 16px 12px; border-bottom: 1px solid #e4e9f1; min-height: 36px; }
.ai-panel__nav-button, .ai-panel__new { display: inline-flex; align-items: center; justify-content: center; gap: 6px; flex-shrink: 0; min-height: 34px; padding: 6px 10px; background: #f6f8fb; border: 1px solid #dde4ee; border-radius: 8px; color: #295ba8; font-weight: 600; font-size: 12px; cursor: pointer; }
.ai-panel__nav-button:hover, .ai-panel__new:hover { background: #edf3ff; }
.ai-panel__nav-button:focus-visible, .ai-panel__new:focus-visible { outline: 2px solid #1769e0; outline-offset: 2px; }
.ai-panel__nav-button:disabled { opacity: .5; cursor: default; }
.ai-panel__new { width: 34px; padding: 0; }
.ai-panel__chat-title { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; font-weight: 600; }
.ai-panel__conversation { display: flex; flex: 1; flex-direction: column; min-height: 0; }
.ai-panel__label { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
.ai-panel__composer { padding: 12px 16px 16px; border-top: 1px solid #e4e9f1; }
.ai-panel__composer textarea { width: 100%; box-sizing: border-box; resize: vertical; min-height: 74px; max-height: 180px; border: 1px solid #d9e1ed; border-radius: 10px; padding: 10px; line-height: 1.5; }
.ai-panel__composer textarea:focus { outline: 2px solid #9ab9ec; outline-offset: 1px; }
.ai-panel__send { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 8px; }
.ai-panel__send small { font-size: 10px; color: #77849a; }
.ai-panel__error { background: #fff5e5; padding: 10px 16px; font-size: 12px; line-height: 1.5; }
.ai-panel__error button { background: transparent; border: 0; text-decoration: underline; color: #295ba8; cursor: pointer; }
.ai-panel__loading { padding: 16px; }
.ai-panel__help { display: flex; flex-shrink: 0; align-items: center; justify-content: center; gap: 24px; margin-top: auto; padding: 10px 16px; border-top: 1px solid #e4e9f1; }
.ai-panel__help button { display: inline-flex; align-items: center; gap: 6px; padding: 4px; border: 0; border-radius: 4px; background: transparent; color: #6b778b; cursor: pointer; font-size: 12px; }
.ai-panel__help button:hover, .ai-panel__help button[aria-expanded="true"] { color: #1769e0; }
.ai-panel__help button:focus-visible { outline: 2px solid #1769e0; outline-offset: 2px; }
</style>
